// ============================================================
// EA USE-CASE HUB SHELL
// Shared chrome for Decision Brief · Scoring Rationale · ROI Model.
// Matches storefront-prototype Full-Content Drilldown v4 hub layout:
// sticky back/rank bar · identity + verdict + KPI header · orange underline tabs.
// ============================================================

import React from 'react';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ChevronRight as ArrowRightIcon,
  Check,
  CheckCircle2,
  Share2,
  FileDown,
  FileText,
  BarChart3,
  TrendingUp,
  Clock,
  Award,
  DollarSign,
} from 'lucide-react';
import { StatTile } from '../StatTile';
import { EffortTagPill, type EffortTag } from '../EffortTagPill';
import { ExecutiveDecisionBanner } from '../VerdictPill';
import { AsOfChip } from './AsOfChip';
import type { VerdictKey } from '../../theme/decisionTokens';
import { VERDICTS } from '../../theme/decisionTokens';

export type HubView = 'brief' | 'rationale' | 'roi';

export interface HubKpi {
  annualBenefit: React.ReactNode;
  npv: React.ReactNode;
  timeToValue: React.ReactNode;
  compositeScore: React.ReactNode;
  portfolioRank: React.ReactNode;
  /** Optional NPV label override (e.g. "5 Year NPV"). Default: "3-Year NPV". */
  npvLabel?: string;
}

export interface EAUseCaseHubShellProps {
  activeView: HubView;
  title: string;
  category?: string | null;
  rank?: number | null;
  portfolioSize: number;
  effortTag?: EffortTag | null;
  inPortfolio: boolean;
  verdict?: VerdictKey | null;
  decisionContent?: React.ReactNode;
  kpis?: HubKpi | null;
  freshnessLabel?: string | null;
  freshnessIsLive?: boolean;
  freshnessTitle?: string;
  backLabel: string;
  onBack: () => void;
  prevId: string | null;
  nextId: string | null;
  onPage: (id: string | null) => void;
  onTab: (view: HubView) => void;
  onShare?: () => void;
  onExport?: () => void;
  exportLabel?: string;
  onProceed: () => void;
  proceedDisabled?: boolean;
  onOpenCommandCenter: () => void;
  children: React.ReactNode;
  /** Extra content under the decision banner (e.g. derived-verdict note). */
  bannerFooter?: React.ReactNode;
  /** Optional max-width class. Default max-w-6xl. */
  maxWidthClass?: string;
  className?: string;
}

const TABS: { key: HubView; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'brief', label: 'Decision Brief', icon: FileText },
  { key: 'rationale', label: 'Scoring Rationale', icon: BarChart3 },
  { key: 'roi', label: 'ROI Model', icon: TrendingUp },
];

export function EAUseCaseHubShell({
  activeView,
  title,
  category,
  rank,
  portfolioSize,
  effortTag,
  inPortfolio,
  verdict,
  decisionContent,
  kpis,
  freshnessLabel,
  freshnessIsLive = true,
  freshnessTitle,
  backLabel,
  onBack,
  prevId,
  nextId,
  onPage,
  onTab,
  onShare,
  onExport,
  exportLabel = 'Export',
  onProceed,
  proceedDisabled = false,
  onOpenCommandCenter,
  children,
  bannerFooter,
  maxWidthClass = 'max-w-6xl',
  className = '',
}: EAUseCaseHubShellProps) {
  const showHeaderCard = Boolean(title);

  return (
    <div className={className}>
      {/* Sticky utility bar — back + title + rank pager (tabs live in the body card). */}
      <div className="sticky top-[72px] z-10 bg-white/95 backdrop-blur border-b border-[#e5e7eb] px-6 py-2.5 print:hidden">
        <div className={`${maxWidthClass} mx-auto flex items-center gap-3`}>
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#717182] hover:text-[#18365E] transition-colors shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {backLabel}
          </button>
          <span className="text-[#e5e7eb]" aria-hidden="true">
            |
          </span>
          <span className="text-xs font-bold text-[#18365E] truncate min-w-0">
            {rank ? `${rank}. ` : ''}
            {title}
          </span>
          <div className="ml-auto flex items-center gap-1 shrink-0">
            <span className="text-[10px] text-[#717182] mr-1.5 hidden sm:block">
              Rank {rank || '—'} of {portfolioSize || '—'}
            </span>
            <button
              type="button"
              onClick={() => onPage(prevId)}
              disabled={!prevId}
              aria-label="Previous ranked opportunity (keeps this tab)"
              className="w-7 h-7 rounded-lg border border-[#e5e7eb] flex items-center justify-center text-[#18365E] disabled:opacity-30 hover:border-[#18365E] transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => onPage(nextId)}
              disabled={!nextId}
              aria-label="Next ranked opportunity (keeps this tab)"
              className="w-7 h-7 rounded-lg border border-[#e5e7eb] flex items-center justify-center text-[#18365E] disabled:opacity-30 hover:border-[#18365E] transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className={`${maxWidthClass} mx-auto px-6 py-6`}>
        {showHeaderCard && (
          <>
            {/* Persistent header: identity, actions, verdict, KPI row */}
            <div className="bg-white rounded-t-xl border border-b-0 border-[#e5e7eb] p-6 pb-5">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    {category && (
                      <span className="text-[10px] font-semibold text-[#FF6E4C] uppercase tracking-wide">
                        {category}
                      </span>
                    )}
                    {effortTag && <EffortTagPill tag={effortTag} />}
                    {inPortfolio && (
                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        In Execute portfolio
                      </span>
                    )}
                  </div>
                  <h1 className="text-2xl font-bold text-[#18365E] leading-tight">{title}</h1>
                </div>

                <div className="flex items-center gap-2 shrink-0 flex-wrap print:hidden">
                  {onShare && (
                    <button
                      type="button"
                      onClick={onShare}
                      className="flex items-center gap-1.5 text-xs font-semibold text-[#18365E] border border-[#e5e7eb] rounded-lg px-3 py-2 hover:border-[#18365E] transition-colors"
                    >
                      <Share2 className="w-3.5 h-3.5" />
                      Share
                    </button>
                  )}
                  {onExport && (
                    <button
                      type="button"
                      onClick={onExport}
                      className="flex items-center gap-1.5 text-xs font-semibold text-[#18365E] border border-[#e5e7eb] rounded-lg px-3 py-2 hover:border-[#18365E] transition-colors"
                    >
                      <FileDown className="w-3.5 h-3.5" />
                      {exportLabel}
                    </button>
                  )}
                  {inPortfolio ? (
                    <button
                      type="button"
                      onClick={onOpenCommandCenter}
                      className="bg-[#18365E] text-white text-xs font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 hover:bg-[#122a4a] transition-colors"
                    >
                      Open Command Center
                      <ArrowRightIcon className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={onProceed}
                      disabled={proceedDisabled}
                      className="bg-[#FF6E4C] text-white text-xs font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 hover:bg-[#e85f3f] disabled:opacity-50 transition-colors"
                    >
                      <Check className="w-3.5 h-3.5" />
                      {verdict ? `${VERDICTS[verdict].verb} with Initiative` : 'Proceed with Initiative'}
                    </button>
                  )}
                </div>
              </div>

              {verdict && decisionContent != null && (
                <div className="mt-4">
                  <ExecutiveDecisionBanner verdict={verdict}>{decisionContent}</ExecutiveDecisionBanner>
                  {bannerFooter}
                </div>
              )}

              {kpis && (
                <>
                  <div className="mt-4 grid grid-cols-2 lg:grid-cols-5 gap-3">
                    <StatTile
                      variant="dominant"
                      label="Annual Benefit"
                      value={kpis.annualBenefit}
                      icon={<TrendingUp className="w-5 h-5" />}
                    />
                    <StatTile
                      label={kpis.npvLabel || '3-Year NPV'}
                      value={kpis.npv}
                      icon={<DollarSign className="w-4 h-4" />}
                    />
                    <StatTile
                      label="Time to Value"
                      value={kpis.timeToValue}
                      icon={<Clock className="w-4 h-4" />}
                    />
                    <StatTile
                      label="Composite Score"
                      value={kpis.compositeScore}
                      icon={<BarChart3 className="w-4 h-4" />}
                    />
                    <StatTile
                      label="Portfolio Rank"
                      value={kpis.portfolioRank}
                      icon={<Award className="w-4 h-4" />}
                    />
                  </div>
                  {(freshnessLabel || freshnessIsLive) && (
                    <div className="mt-2.5 flex items-center justify-end">
                      <AsOfChip
                        label={freshnessLabel || undefined}
                        isLive={freshnessIsLive}
                        title={freshnessTitle}
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Tabs + body */}
            <div className="border border-t-0 border-[#e5e7eb] rounded-b-xl bg-[#F5F7FA]">
              <div className="flex items-center gap-1 border-b border-[#e5e7eb] bg-white px-3 print:hidden">
                {TABS.map((t) => {
                  const Icon = t.icon;
                  const active = activeView === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => onTab(t.key)}
                      aria-current={active ? 'page' : undefined}
                      className={`flex items-center gap-1.5 px-3.5 py-3 text-xs font-semibold border-b-2 -mb-px transition-colors ${
                        active
                          ? 'border-[#FF6E4C] text-[#FF6E4C]'
                          : 'border-transparent text-[#717182] hover:text-[#18365E]'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {t.label}
                    </button>
                  );
                })}
                {inPortfolio && (
                  <button
                    type="button"
                    onClick={onOpenCommandCenter}
                    className="ml-auto flex items-center gap-1.5 px-3.5 py-3 text-xs font-bold text-[#18365E] hover:text-[#FF6E4C] transition-colors"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Command Center
                    <ArrowRightIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <div className="p-5">{children}</div>
            </div>
          </>
        )}

        {!showHeaderCard && <div className="p-5">{children}</div>}
      </div>
    </div>
  );
}

export default EAUseCaseHubShell;
